import React, { useEffect, useState } from "react";
import { Form, Formik, FormikHelpers } from "formik";
import { object, string, array } from "yup";
import { InquiryFormValues } from "src/models/Inquiry.model";
import InquiryForm from "../Layouts/InquiryForm";
import SideNavLayout from "src/components/layouts/SideNavLayout/SideNavLayout";
import { useAddInquiryMutation } from "src/services/InquiryServices";
import { showToast } from "src/utils/showToaster";
import { useNavigate } from "react-router-dom";
import useCountries from "src/hooks/useCountries";
import useCountryStates from "src/hooks/useCountryStates";
import useStateCities from "src/hooks/useStateCities";
import { useGetStudentDetailByNumberQuery } from "src/services/StudentServices";
import { mobileRegExp } from "src/utils/validations/indianPhoneRegex";

const AddInquiryFormWrapper = () => {
  const [addInquiryForm] = useAddInquiryMutation();
  const [toRedirectNext, setToRedirectNext] = useState(false);
  const navigate = useNavigate();
  const [defaultCountryId, setDefaultCountryId] = useState<any[]>([]);
  const [defaultStateId, setDefaultStateId] = useState<any[]>([]);
  const [defaultCityId, setDefaultCityId] = useState<any[]>([]);
  const [inquiredNumber, setInquiredNumber] = useState("");
  const [inquiryDetail, setInquiryDetail] = useState<any>({});

  // Step 1: Remove the country code if present
  const withoutCountryCode = inquiredNumber?.replace(/^\+91\s?/, "");

  // Step 2: Remove all spaces
  const finalNumber = withoutCountryCode?.replace(/\s/g, "");

  //STUDENT DETAIL GET BY ID
  const {
    data: inquiredData,
    isLoading: inquiredDataIsLoading,
    isFetching: inquiredDataIsFetching,
  } = useGetStudentDetailByNumberQuery(finalNumber, {
    skip: !finalNumber,
  });

  useEffect(() => {
    if (!inquiredDataIsLoading && !inquiredDataIsFetching) {
      setInquiryDetail(inquiredData?.data?.[0]);
    }
  }, [inquiredData, inquiredDataIsLoading, inquiredDataIsFetching]);

  // Default countrie
  const { countries, isDataLoading: isCountriesLoading } = useCountries();
  useEffect(() => {
    if (!isCountriesLoading) {
      setDefaultCountryId(countries?.filter((el) => el?.isDefault) || []);
    }
  }, [isCountriesLoading, countries]);

  // Default State
  const { countryStates, isDataLoading: isCountryStatesLoading } =
    useCountryStates(defaultCountryId?.[0]?._id || "");

  useEffect(() => {
    if (!isCountryStatesLoading) {
      setDefaultStateId(countryStates?.filter((el) => el?.isDefault) || []);
    }
  }, [isCountryStatesLoading, countryStates]);

  // Default City

  const { stateCities, isDataLoading: isStateCitiesLoading } = useStateCities(
    defaultStateId?.[0]?._id
  );

  useEffect(() => {
    if (!isStateCitiesLoading) {
      setDefaultCityId(stateCities?.filter((el) => el?.isDefault) || []);
    }
  }, [isStateCitiesLoading, stateCities]);
  // Form Initial Values
  const initialValues: InquiryFormValues = {
    name: inquiryDetail?.name || "",
    inquiryLabel :'' ,
    disposition_one: '',
    disposition_two: '',
    mobile: inquiryDetail?.mobile || "",
    email: inquiryDetail?.email || "",
    instagram_id: inquiryDetail?.instagram_id || "",
    facebook_id: inquiryDetail?.facebook_id || "",
    youtube_id: inquiryDetail?.youtube_id || "",
    twitter_id: inquiryDetail?.twitter_id || "",
    linkedin_id: inquiryDetail?.linkedin_id || "",
    inquiryType: '',
    whatsApp_mobile: inquiryDetail?.whatsApp_mobile || "",
    alternate_number_1: inquiryDetail?.alternate_number_1 || "",
    alternate_number_2: inquiryDetail?.alternate_number_2 || "",
    interested_course: inquiryDetail?.interested_course?.length
      ? inquiryDetail?.interested_course?.map((el: any) => {
        return {
          course_name: {
            label: el?.courseLabel?.courseName,
            value: el?.course_name,
          },
          fees: el?.fees?.toString(),
          Offer_Price: el?.Offer_Price,
          remark: el?.remark,
        };
      })
      : [
        {
          course_name: "",
          fees: "",
          Offer_Price: "",
          remark: "",
        },
      ],
    communication_type:
      {
        label: inquiryDetail?.communication_type,
        value: inquiryDetail?.communication_type,
      } || {},
    communication_remark: inquiryDetail?.communication_remark || "",
    communication_summary: inquiryDetail?.communication_summary || "",
    inquired_for: inquiryDetail?.inquired_for || [],
    any_one_contact: "",
    countryId: inquiryDetail?.cityId
      ? {
        label: inquiryDetail?.countryLabel,
        value: inquiryDetail?.countryId,
      }
      : defaultCountryId?.map((el: any) => {
        return {
          label: el?.countryName,
          value: el?._id,
        };
      })[0],
    stateId: inquiryDetail?.cityId
      ? {
        label: inquiryDetail?.stateLabel,
        value: inquiryDetail?.stateId,
      }
      : defaultStateId?.map((el: any) => {
        return {
          label: el?.stateName,
          value: el?._id,
        };
      })[0],
    cityId: inquiryDetail?.cityId
      ? {
        label: inquiryDetail?.cityLabel,
        value: inquiryDetail?.cityId,
      }
      : defaultCityId?.map((el: any) => {
        return {
          label: el?.cityName,
          value: el?._id,
        };
      })[0],
  };

  // Validation Schema
  const validationSchema = object()
    .shape({
      name: string().required("please enter name"),
      mobile: string()
        .trim()
        .matches(mobileRegExp, "mobile number is not valid"),
      email: string().email(),
      whatsApp_mobile: string()
        .trim()
        .matches(mobileRegExp, "whatsApp mobile number is not valid"),
      alternate_number_1: string()
        .trim()
        .matches(mobileRegExp, "number is not valid"),
      alternate_number_2: string()
        .trim()
        .matches(mobileRegExp, "number is not valid"),
      inquiryType: object().required("please select inquiry type"),
      interested_course: array().of(
        object({
          course_name: object().required("please select course name"),
          fees: string().required("please enter total fees"),
          Offer_Price: string().required("please enter offer fees"),
        })
      ),
      instagram_id: string(),
      facebook_id: string(),
      youtube_id: string(),
      twitter_id: string(),
      linkedin_id: string(),
      marital_status: string(),
      gender: string(),
      communication_remark: string(),
      communication_summary: string().required("please enter communication summary"),
      inquired_for: array(),
    })
    .test("test_contactsInfo", "", (value, context: any) => {
      const {
        mobile,
        email,
        instagram_id,
        facebook_id,
        youtube_id,
        twitter_id,
        linkedin_id,
      } = context?.options?.context;
      if (
        !(
          mobile ||
          email ||
          instagram_id ||
          facebook_id ||
          youtube_id ||
          twitter_id ||
          linkedin_id
        )
      ) {
        return context.createError({
          path: "any_one_contact",
          message: "Required",
        });
      }
    });

  // Handle Submit
  const handleSubmit = (
    values: InquiryFormValues,
    { setSubmitting, resetForm }: FormikHelpers<InquiryFormValues>,
    next?: (res: any) => void
  ) => {
    const formatedValue = values.interested_course?.map((el: any) => {
      return {
        course_name:
          el?.course_name === ""
            ? null
            : el?.course_name?.value
              ? el?.course_name?.value
              : el?.course_name,
        fees: el?.fees?.toString(),
        Offer_Price: el?.Offer_Price,
        remark: el?.remark,
      };
    }); 

    const formFormattedvalues :any ={
      name: values.name,
      inquiryLabelId :values?.inquiryLabel?.value ,
      mobile: finalNumber,
      email: values.email,
      whatsApp_mobile: values?.whatsApp_mobile,
      alternate_number_1: values?.alternate_number_1,
      alternate_number_2: values?.alternate_number_2,
      instagram_id: values.instagram_id,
      inquiryType: values.inquiryType?.value,
      facebook_id: values.facebook_id,
      youtube_id: values.youtube_id,
      twitter_id: values.twitter_id,
      linkedin_id: values.linkedin_id,
      interested_course: formatedValue as any,
      communication_type: values.communication_type?.value,
      communication_remark: values.communication_remark,
      communication_summary: values?.communication_summary,
      inquired_for: values?.inquired_for || [],
      countryId: values?.countryId?.value,
      stateId: values?.stateId?.value,
      cityId: values?.cityId?.value,
      
    }

   
      addInquiryForm(formFormattedvalues)
        .then((res: any) => {
          if (res?.error) {
            showToast("error", res?.error?.data?.message);
          } else {
            if (res?.data?.status) {
              showToast("success", res?.data?.message);
              toRedirectNext
                ? navigate(
                  `/inquiries/edit/${res?.data?.data?._id}/details/personal-information`
                )
                : navigate("/inquiries");
              resetForm();
              setToRedirectNext(false);
            } else {
              showToast("error", res?.data?.message);
            }
          }
          setSubmitting(false);
        })
        .catch((err) => {
          console.error("error", err);
        });
   
  };

  return (
    <SideNavLayout>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values, formikHelpers, next?: (res: any) => void) =>
          handleSubmit(values, formikHelpers, next)
        }
      >
        {(formikProps) => (
          <Form>
            <InquiryForm
              onRedirect={() => setToRedirectNext(true)}
              formikProps={formikProps}
              setInquiredNumber={setInquiredNumber}
            />
          </Form>
        )}
      </Formik>
    </SideNavLayout>
  );
};

export default AddInquiryFormWrapper;
